import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { AppRuntime } from './bootstrap';

/**
 * Контекст с AppRuntime — единственное, ради чего экранам вообще нужен доступ
 * к `src/app/`: RestClient для создания/поиска комнаты и connectToRoom, чтобы
 * подключиться к ней после. Сам bootstrap() остаётся недоступен напрямую.
 *
 * `null` значит, что bootstrap() ещё не успел отработать (первый кадр
 * рендера). Экраны обязаны это пережить: показать, что создание комнаты
 * недоступно, а не упасть.
 */
const RuntimeContext = createContext<AppRuntime | null>(null);

export interface RuntimeProviderProps {
  /** Промис из bootstrap() — main.tsx вызывает его один раз и передаёт сюда. */
  runtime: Promise<AppRuntime>;
  children: ReactNode;
}

export function RuntimeProvider({ runtime, children }: RuntimeProviderProps) {
  const [value, setValue] = useState<AppRuntime | null>(null);

  useEffect(() => {
    let cancelled = false;
    runtime.then((resolved) => {
      if (!cancelled) setValue(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [runtime]);

  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

/** AppRuntime текущего приложения. См. комментарий у RuntimeContext про `null`. */
export function useAppRuntime(): AppRuntime | null {
  return useContext(RuntimeContext);
}

// Тестам нужен способ подставить готовый AppRuntime синхронно, без промиса и
// без ожидания эффекта — RuntimeProvider для этого не годится.
export { RuntimeContext };
